/**
 * Pure helpers that turn a thrown API error into the copy a loader banner should show.
 *
 * WHY THIS EXISTS (card 53650). `web/services/api.js` is a `fetch` client, NOT axios.
 * `handleResponse` throws a plain `Error` carrying `.message` (already resolved to
 * `data.error || data.message`), plus `.status`, `.code`, `.errors` and `.data` — and it
 * assigns `.response` nowhere in the file. So every `err.response?.data?.message` read in
 * `web/` evaluates to `undefined` 100% of the time, and any banner sourced that way is
 * permanently pinned to its hardcoded fallback. That silently swallowed the one class of
 * failure a user most needs named: `api/middleware/auth.js` answers 401 with
 * `Account is deactivated`, `Account is temporarily locked` and `Invalid or expired token`,
 * and `api.js` clears the tokens on 401 — so the member saw a generic "we could not load"
 * plus a Retry that could never succeed.
 */

// `api.js` builds these itself when the body carries no server prose: `Request failed`
// (`:254`) and `Request failed with status <n>` (`:247`). They are CLIENT filler, not
// something the server said, so they must never reach a banner.
const CLIENT_PLACEHOLDER_PREFIX = 'Request failed';

/**
 * A 4xx is the server addressing THIS request — expired session, locked account, missing
 * record, rate limit — so its prose is worth more than our generic copy. A 5xx, or a
 * transport failure that never reached the server (no `.status` at all), is ours: the
 * page's own friendly sentence reads better than an internal error string.
 */
export const isClientActionableError = (error) => {
  const status = error?.status;
  return Number.isInteger(status) && status >= 400 && status < 500;
};

export const hasServerMessage = (error) => {
  const message = error?.message;
  return typeof message === 'string'
    && message.trim().length > 0
    && !message.startsWith(CLIENT_PLACEHOLDER_PREFIX);
};

export const resolveLoadErrorMessage = (error, fallback) =>
  isClientActionableError(error) && hasServerMessage(error) ? error.message : fallback;
